import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, Paper, Button, Tabs, Tab, Grid, Card, CardContent, CardActions,
  Chip, Avatar, Divider, Grow, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, TextField, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Autocomplete, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import FolderIcon from '@mui/icons-material/Folder';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import API_URL from '../../config';
import Loading from '../../components/Loading';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`quote-tabpanel-${index}`} aria-labelledby={`quote-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3, px: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>{children}</Box>}
    </div>
  );
}

function QuoteDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Followup states
  const [quoteFollowups, setQuoteFollowups] = useState([]);
  const [quoteConversations, setQuoteConversations] = useState([]);
  const [quoteDocuments, setQuoteDocuments] = useState([]);
  const [quoteAssignments, setQuoteAssignments] = useState([]);
  const [quoteMilestones, setQuoteMilestones] = useState([]);

  const [newFollowup, setNewFollowup] = useState('');
  const [newFollowupDesc, setNewFollowupDesc] = useState('');
  const [newFollowupStartDate, setNewFollowupStartDate] = useState('');
  const [newFollowupStartTime, setNewFollowupStartTime] = useState('');
  const [newFollowupEndDate, setNewFollowupEndDate] = useState('');
  const [newFollowupEndTime, setNewFollowupEndTime] = useState('');
  const [newFollowupNextVisitStartDate, setNewFollowupNextVisitStartDate] = useState('');
  const [newFollowupNextVisitStartTime, setNewFollowupNextVisitStartTime] = useState('');
  const [newFollowupNextVisitEndDate, setNewFollowupNextVisitEndDate] = useState('');
  const [newFollowupNextVisitEndTime, setNewFollowupNextVisitEndTime] = useState('');
  const [newFollowupAssignee, setNewFollowupAssignee] = useState('');
  const [openFollowupDialog, setOpenFollowupDialog] = useState(false);
  const [editingFollowupId, setEditingFollowupId] = useState(null);

  const [newConversation, setNewConversation] = useState('');
  const [newAssignment, setNewAssignment] = useState('');
  const [newMilestone, setNewMilestone] = useState('');
  const [newMilestoneDate, setNewMilestoneDate] = useState('');

  const [users, setUsers] = useState([]);
  const [openMilestoneDialog, setOpenMilestoneDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [winPrediction, setWinPrediction] = useState(null);

  // AI Analyze states
  const [aiAnalyzeLoading, setAiAnalyzeLoading] = useState(false);
  const [aiAnalyzeData, setAiAnalyzeData] = useState(null);
  const [aiAnalyzeError, setAiAnalyzeError] = useState(null);

  // SHAP impacts from batch-predict (feature contributions)
  const [shapImpacts, setShapImpacts] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchQuoteDetail();
    fetchUsers();
    fetchFollowups();
    fetchConversations();
    fetchDocuments();
    fetchAssignments();
    fetchMilestones();
  }, [id]);

  useEffect(() => {
    if (quote && quote.statecode !== 2 && quote.statecode !== 3) {
      fetchWinPrediction(quote.quoteid);
    } else {
      setWinPrediction(null);
    }
  }, [quote]);

  // Auto-call AI Analyze when tab 7 is selected
  useEffect(() => {
    if (tabValue === 7 && quote && !aiAnalyzeData && !aiAnalyzeLoading) {
      fetchAiAnalyze();
    }
  }, [tabValue, quote]);

  const fetchQuoteDetail = async () => {
    try {
      const res = await axios.get(`${API_URL}/quotes/${id}`, { headers });
      setQuote(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/setup/users`, { headers });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchFollowups = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/followups`, { headers });
      setQuoteFollowups(response.data);
    } catch (error) { console.error('Error fetching followups:', error); }
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/conversations`, { headers });
      setQuoteConversations(response.data);
    } catch (error) { console.error('Error fetching conversations:', error); }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/documents`, { headers });
      setQuoteDocuments(response.data);
    } catch (error) { console.error('Error fetching documents:', error); }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/assignments`, { headers });
      setQuoteAssignments(response.data);
    } catch (error) { console.error('Error fetching assignments:', error); }
  };

  const fetchMilestones = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}/milestones`, { headers });
      setQuoteMilestones(response.data);
    } catch (error) { console.error('Error fetching milestones:', error); }
  };

  const fetchWinPrediction = async (quoteId) => {
    try {
      const res = await axios.post(`${API_URL}/quotes/batch-predict`, { quote_ids: [quoteId] }, { headers });
      const preds = res.data.predictions || {};
      const prediction = preds[(quoteId || '').toUpperCase()] || preds[quoteId] || null;
      setWinPrediction(prediction);
      // Capture shap_impacts from the prediction response
      if (prediction && prediction.shap_impacts) {
        setShapImpacts(prediction.shap_impacts);
      }
    } catch (err) {
      console.error('Win prediction fetch error:', err);
      setWinPrediction(null);
    }
  };

  const fetchAiAnalyze = async () => {
    setAiAnalyzeLoading(true);
    setAiAnalyzeError(null);
    setAiAnalyzeData(null);
    try {
      const predictionProb = winPrediction && winPrediction.win_probability != null
        ? winPrediction.win_probability / 100.0
        : 0.0;
      const payload = {
        quote_id: quote.quoteid,
        prediction_probability: predictionProb,
      };
      // Include SHAP impacts if available from batch-predict
      if (shapImpacts) {
        payload.shap_impacts = shapImpacts;
      }
      const res = await axios.post(`${API_URL}/ai-reports/analyze_quotation`, payload, { headers });
      if (res.data && res.data.status === 'success') {
        setAiAnalyzeData(res.data);
      } else {
        setAiAnalyzeError(res.data?.message || 'AI analysis returned an unexpected response.');
      }
    } catch (err) {
      setAiAnalyzeError(err.response?.data?.message || err.message || 'Failed to fetch AI analysis.');
    } finally {
      setAiAnalyzeLoading(false);
    }
  };

  const handleAddFollowup = async () => {
    if (!newFollowup) return;
    try {
      const reminder_date = (newFollowupStartDate && newFollowupStartTime) ? new Date(`${newFollowupStartDate}T${newFollowupStartTime}`).toISOString() : null;
      const date_time_end = (newFollowupEndDate && newFollowupEndTime) ? new Date(`${newFollowupEndDate}T${newFollowupEndTime}`).toISOString() : null;
      const next_visit = (newFollowupNextVisitStartDate && newFollowupNextVisitStartTime) ? new Date(`${newFollowupNextVisitStartDate}T${newFollowupNextVisitStartTime}`).toISOString() : null;
      const next_visit_end = (newFollowupNextVisitEndDate && newFollowupNextVisitEndTime) ? new Date(`${newFollowupNextVisitEndDate}T${newFollowupNextVisitEndTime}`).toISOString() : null;
      const payload = { note: newFollowup, description: newFollowupDesc, reminder_date, date_time_end, next_visit, next_visit_end, assigned_to_id: newFollowupAssignee || null };
      if (editingFollowupId) {
        await axios.put(`${API_URL}/quotes/${id}/followups/${editingFollowupId}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/quotes/${id}/followups`, payload, { headers });
      }
      resetFollowupForm();
      setOpenFollowupDialog(false);
      fetchFollowups();
    } catch (error) { console.error('Error adding/updating followup:', error); }
  };

  const resetFollowupForm = () => {
    setNewFollowup(''); setNewFollowupDesc(''); setNewFollowupStartDate(''); setNewFollowupStartTime('');
    setNewFollowupEndDate(''); setNewFollowupEndTime(''); setNewFollowupNextVisitStartDate(''); setNewFollowupNextVisitStartTime('');
    setNewFollowupNextVisitEndDate(''); setNewFollowupNextVisitEndTime(''); setNewFollowupAssignee(''); setEditingFollowupId(null);
  };

  const handleEditFollowup = (f) => {
    setNewFollowup(f.note);
    setNewFollowupDesc(f.description || '');
    if (f.reminder_date) { const d = new Date(f.reminder_date); setNewFollowupStartDate(d.toISOString().substring(0, 10)); setNewFollowupStartTime(d.toTimeString().substring(0, 5)); } else { setNewFollowupStartDate(''); setNewFollowupStartTime(''); }
    if (f.date_time_end) { const de = new Date(f.date_time_end); setNewFollowupEndDate(de.toISOString().substring(0, 10)); setNewFollowupEndTime(de.toTimeString().substring(0, 5)); } else { setNewFollowupEndDate(''); setNewFollowupEndTime(''); }
    if (f.next_visit) { const nv = new Date(f.next_visit); setNewFollowupNextVisitStartDate(nv.toISOString().substring(0, 10)); setNewFollowupNextVisitStartTime(nv.toTimeString().substring(0, 5)); } else { setNewFollowupNextVisitStartDate(''); setNewFollowupNextVisitStartTime(''); }
    if (f.next_visit_end) { const nve = new Date(f.next_visit_end); setNewFollowupNextVisitEndDate(nve.toISOString().substring(0, 10)); setNewFollowupNextVisitEndTime(nve.toTimeString().substring(0, 5)); } else { setNewFollowupNextVisitEndDate(''); setNewFollowupNextVisitEndTime(''); }
    setNewFollowupAssignee(f.assigned_to_id || '');
    setEditingFollowupId(f.id);
    setOpenFollowupDialog(true);
  };

  const handleAddConversation = async () => {
    if (!newConversation) return;
    try {
      await axios.post(`${API_URL}/quotes/${id}/conversations`, { message: newConversation }, { headers });
      setNewConversation('');
      fetchConversations();
    } catch (error) { console.error('Error adding conversation:', error); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      await axios.post(`${API_URL}/quotes/${id}/documents`, { file_name: file.name, file_path: `/uploads/${file.name}` }, { headers });
      fetchDocuments();
    } catch (error) { console.error('Error uploading document:', error); }
    finally { setUploadingFile(false); }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone) return;
    try {
      await axios.post(`${API_URL}/quotes/${id}/milestones`, { name: newMilestone, due_date: newMilestoneDate ? new Date(newMilestoneDate).toISOString() : null, status: 'Pending' }, { headers });
      setNewMilestone(''); setNewMilestoneDate(''); setOpenMilestoneDialog(false);
      fetchMilestones();
    } catch (error) { console.error('Error adding milestone:', error); }
  };

  const handleAssignUser = async () => {
    setAssignLoading(true);
    try {
      await axios.post(`${API_URL}/quotes/${id}/assignments`, { user_id: newAssignment }, { headers });
      setOpenAssignDialog(false); setNewAssignment('');
      fetchAssignments();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Error assigning user");
    } finally { setAssignLoading(false); }
  };

  if (loading || !quote) return <Loading message="Loading Quote Details..." />;

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('won')) return 'success';
    if (s.includes('lost')) return 'error';
    if (s.includes('active')) return 'primary';
    if (s.includes('draft')) return 'default';
    return 'info';
  };

  // AI Analyze tab is disabled when win % is not showing
  const isWinPercentShowing =
    quote?.statecode === 2 ||
    quote?.statecode === 3 ||
    (winPrediction && winPrediction.win_probability != null && !winPrediction.inactive);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
      {/* Modern Gradient Header */}
      <Grow in={true} timeout={500}>
        <Paper elevation={0} sx={{ mb: 4, mt: 2, p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)', position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-start' }, justifyContent: 'space-between', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotes')} sx={{ color: '#90caf9', mb: 2, pl: 0, fontWeight: 'bold', '&:hover': { color: 'white', bgcolor: 'transparent', transform: 'translateX(-4px)' }, transition: 'all 0.2s' }}>Back to Quotes</Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar sx={{ width: 52, height: 52, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.15)', color: '#e3f2fd', boxShadow: '0 6px 18px rgba(0,0,0,0.2)', border: '2px solid rgba(255,255,255,0.2)' }}>
                    <RequestQuoteIcon sx={{ color: 'white', fontSize: 26 }} />
                  </Avatar>
                  <Typography variant="h5" fontWeight="900" sx={{ letterSpacing: 0.3, wordBreak: 'break-word', color: 'white', lineHeight: 1.3 }}>{quote.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ml: 0.5, mt: 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', alignItems: 'center', gap: 0.8, 
                    bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
                    px: 1.8, py: 0.7, borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: (quote.statecode === 2 || (quote.status || '').toLowerCase().includes('won')) ? '#4ade80' : (quote.statecode === 3 || (quote.status || '').toLowerCase().includes('lost')) ? '#f87171' : '#facc15', boxShadow: `0 0 6px ${(quote.statecode === 2 || (quote.status || '').toLowerCase().includes('won')) ? '#4ade80' : (quote.statecode === 3 || (quote.status || '').toLowerCase().includes('lost')) ? '#f87171' : '#facc15'}` }} />
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.3, fontSize: '0.75rem' }}>
                      {quote.status || 'Draft'}
                    </Typography>
                  </Box>
                  {quote.customer_name && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <BusinessIcon sx={{ color: '#93c5fd', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.customer_name}
                        </Typography>
                      </Box>
                  )}
                  {quote.quote_ref && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <RequestQuoteIcon sx={{ color: '#c4b5fd', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.quote_ref}
                        </Typography>
                      </Box>
                  )}
                  {quote.quote_number && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <AssignmentIcon sx={{ color: '#fde68a', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.quote_number}
                        </Typography>
                      </Box>
                  )}
                  {quote.country && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <PublicIcon sx={{ color: '#86efac', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.country}
                        </Typography>
                      </Box>
                  )}
                  {quote.division && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.12)',
                      }}>
                        <FolderIcon sx={{ color: '#fde68a', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.75rem' }}>
                          {quote.division}
                        </Typography>
                      </Box>
                  )}
                  {isWinPercentShowing && (
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', gap: 0.8, 
                        bgcolor: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
                        px: 1.8, py: 0.7, borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}>
                        <CheckCircleIcon sx={{ color: (quote.statecode === 2 || (winPrediction && winPrediction.win_probability >= 50)) ? '#4ade80' : '#f87171', fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.3, fontSize: '0.75rem' }}>
                          {quote.statecode === 2 ? 'Won 100%' : quote.statecode === 3 ? 'Lost 0%' : `Win: ${winPrediction?.win_probability}%`}
                        </Typography>
                      </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grow>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', minHeight: '400px', bgcolor: 'white', border: '1px solid #f0f0f0' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} variant="scrollable" scrollButtons="auto" TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }} sx={{ borderBottom: 1, borderColor: '#f0f0f0', bgcolor: 'white', px: 2, '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', minHeight: 64, fontSize: '0.95rem' } }}>
              <Tab icon={<InfoIcon />} label="Quote Details" iconPosition="start" />
              <Tab icon={<ShoppingCartIcon />} label="Products" iconPosition="start" />
              <Tab icon={<CalendarTodayIcon />} label="Followups" iconPosition="start" />
              <Tab icon={<ForumIcon />} label="Conversations" iconPosition="start" />
              <Tab icon={<CloudUploadIcon />} label="Documents" iconPosition="start" />
              <Tab icon={<PersonAddIcon />} label="Assignments" iconPosition="start" />
              <Tab icon={<FlagIcon />} label="Milestones" iconPosition="start" />
              <Tab icon={<PsychologyIcon />} label="AI Analyze" iconPosition="start" disabled={!isWinPercentShowing} />
            </Tabs>

            {/* Tab 0: Quote Details */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><BusinessIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Customer Information</Typography></Box>
                        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                          <Typography variant="body1" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>{quote.customer_name || 'N/A'}</Typography>
                          <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><EmailIcon sx={{ fontSize: 18, color: '#94a3b8' }} /><Typography variant="body2" color="#64748b">{quote.customer_email || 'N/A'}</Typography></Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon sx={{ fontSize: 18, color: '#94a3b8' }} /><Typography variant="body2" color="#64748b">{quote.customer_phone || 'N/A'}</Typography></Box>
                          {quote.account_number && <><Divider sx={{ my: 2, borderColor: '#e2e8f0' }} /><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Account Number</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb' }}>{quote.account_number}</Typography></>}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><RequestQuoteIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote Summary</Typography></Box>
                        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Subtotal</Typography><Typography variant="body1" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{formatCurrency(quote.subtotal)}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Tax</Typography><Typography variant="body1" sx={{ fontWeight: 700, color: '#f59e0b', mt: 0.5 }}>{quote.tax_percentage > 0 ? `${quote.tax_percentage}%` : formatCurrency(quote.total_tax)}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Total Amount</Typography><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}><AttachMoneyIcon sx={{ fontSize: 22, color: '#10b981' }} /><Typography variant="h6" sx={{ fontWeight: '800', color: '#10b981' }}>{formatCurrency(quote.total_amount)}</Typography></Box></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Status</Typography><Box sx={{ mt: 0.5 }}><Chip label={quote.status || 'Draft'} color={getStatusColor(quote.status)} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5 }} /></Box></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Created On</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{formatDate(quote.createdon)}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Quote Ref</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', mt: 0.5 }}>{quote.quote_ref || quote.quote_number || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Country</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{quote.country || '—'}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Division</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', mt: 0.5 }}>{quote.division || '—'}</Typography></Grid>
                            {quote.subtotal > 0 || quote.total_tax > 0 ? (
                              <>
                                <Grid item xs={12}><Divider sx={{ borderColor: '#e2e8f0' }} /></Grid>
                                <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Grand Total</Typography><Typography variant="h6" sx={{ fontWeight: '800', color: '#10b981', mt: 0.5 }}>{formatCurrency(quote.grand_total)}</Typography></Grid>
                                {quote.total_discount > 0 && <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Discount</Typography><Typography variant="body1" sx={{ fontWeight: 700, color: '#ef4444', mt: 0.5 }}>-{formatCurrency(quote.total_discount)}</Typography></Grid>}
                              </>
                            ) : null}
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  {quote.opportunity_name && (
                    <Grid item xs={12} md={6}>
                      <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                        <CardContent sx={{ p: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><WorkIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Opportunity Details</Typography></Box>
                          <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <Typography variant="body1" sx={{ fontWeight: 800, color: '#0f172a', mb: 2 }}>{quote.opportunity_name}</Typography>
                            <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                            <Grid container spacing={2}>
                              <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Created</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>{formatDate(quote.opportunity_createdon)}</Typography></Grid>
                              <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Est. Close Date</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>{formatDate(quote.opportunity_estimated_close_date)}</Typography></Grid>
                              {quote.opportunity_estimated_value && <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Est. Value</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981', mt: 0.5 }}>{formatCurrency(quote.opportunity_estimated_value)}</Typography></Grid>}
                              {quote.opportunity_industry && <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Industry</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>{quote.opportunity_industry}</Typography></Grid>}
                              {quote.opportunity_area && <Grid item xs={6}><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Area</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5 }}>{quote.opportunity_area}</Typography></Grid>}
                            </Grid>
                            {quote.opportunity_description && <><Divider sx={{ my: 2, borderColor: '#e2e8f0' }} /><Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Description</Typography><Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>{quote.opportunity_description}</Typography></>}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                  {quote.description && (
                    <Grid item xs={12} md={quote.opportunity_name ? 6 : 6}>
                      <Card elevation={0} sx={{ borderRadius: 4, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                        <CardContent sx={{ p: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><DescriptionIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote Description</Typography></Box>
                          <Box sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}><Typography variant="body2" sx={{ color: '#475569', whiteSpace: 'pre-wrap' }}>{quote.description}</Typography></Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </TabPanel>

            {/* Tab 1: Products */}
            <TabPanel value={tabValue} index={1}>
              {quote.line_items && quote.line_items.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Grid item xs={12}>
                    <Card elevation={0} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', bgcolor: '#ffffff' }}>
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}><ShoppingCartIcon sx={{ color: '#3b82f6' }} /><Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Quote Products ({quote.line_items.length})</Typography></Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }}>Product</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }}>Description</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Qty</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Price/Unit</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Discount</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Tax</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }} align="right">Amount</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#475569', fontSize: '0.75rem' }}>Type</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {quote.line_items.map((item, idx) => (
                                <TableRow key={idx} hover>
                                  <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{item.product_name || '—'}</TableCell>
                                  <TableCell sx={{ color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_description || '—'}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, color: '#334155' }}>{item.quantity ?? '—'}</TableCell>
                                  <TableCell align="right" sx={{ color: '#334155' }}>{item.price_per_unit ? formatCurrency(item.price_per_unit) : '—'}</TableCell>
                                  <TableCell align="right" sx={{ color: '#ef4444' }}>{item.discount ? formatCurrency(item.discount) : '—'}</TableCell>
                                  <TableCell align="right" sx={{ color: '#f59e0b' }}>{item.tax ? formatCurrency(item.tax) : '—'}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 700, color: '#10b981' }}>{item.extended_amount ? formatCurrency(item.extended_amount) : '—'}</TableCell>
                                  <TableCell><Chip label={item.product_type || 'N/A'} size="small" sx={{ borderRadius: 1.5, fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Box>
              ) : (
                <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#f8fafc' }}>
                  <ShoppingCartIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No Products</Typography>
                  <Typography variant="body2" color="#94a3b8">This quote does not have any products/line items in Dynamics 365 CRM.</Typography>
                </Paper>
              )}
            </TabPanel>

            {/* Tab 2: Followups */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Follow-Ups</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetFollowupForm(); setOpenFollowupDialog(true); }} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' } }}>Log Follow-up</Button>
                </Box>
                <Box>
                  {quoteFollowups.length === 0 ? (
                    <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: "#f8fafc" }}>
                      <EventNoteIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                      <Typography variant="h6" color="#64748b" fontWeight="700" mb={1}>No follow-ups recorded</Typography>
                      <Typography variant="body2" color="#94a3b8">Log your interactions and upcoming activities with this quote.</Typography>
                    </Paper>
                  ) : (
                    <Box sx={{ position: 'relative', pl: { xs: 2.5, md: 5 }, pr: { xs: 1, md: 3 }, '&::before': { content: '""', position: 'absolute', top: 0, bottom: 0, left: { xs: '10px', md: '20px' }, width: '3px', bgcolor: '#e2e8f0', borderRadius: 3, zIndex: 0 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {quoteFollowups.map(item => {
                        const isAuto = item.note && item.note.startsWith("[Next Visit]");
                        return (
                          <Box key={item.id} sx={{ position: 'relative', zIndex: 1, mb: 1 }}>
                            <Box sx={{ position: 'absolute', left: { xs: -24, md: -45 }, top: 24, width: 18, height: 18, borderRadius: '50%', background: isAuto ? 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: '4px solid #ffffff', boxShadow: '0 0 0 2px #e2e8f0' }} />
                            <Card elevation={0} sx={{ border: '1px solid', borderColor: isAuto ? '#ede9fe' : '#e2e8f0', borderRadius: 4, transition: 'all 0.3s ease', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', '&:hover': { borderColor: isAuto ? '#c4b5fd' : '#cbd5e1', boxShadow: '0 10px 30px rgba(0,0,0,0.06)', transform: 'translateY(-3px)' }, overflow: 'hidden' }}>
                              <Box sx={{ p: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, borderBottom: '1px solid', borderColor: isAuto ? '#ede9fe' : '#f1f5f9', background: isAuto ? 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ bgcolor: isAuto ? '#8b5cf6' : '#2563eb', color: 'white', width: 44, height: 44, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>{isAuto ? <AccessTimeIcon /> : <EventNoteIcon />}</Avatar>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="800" color="#1e293b">
                                      {item.note && isAuto ? item.note.replace('[Next Visit] ', '') : (item.created_at ? new Date(item.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'No Start Date')}
                                    </Typography>
                                    <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                      {isAuto ? <Chip label="Auto-Generated" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#e0e7ff', color: '#4f46e5' }} /> : null}
                                      {item.assigned_to_name && <Typography variant="caption" color="#64748b" fontWeight="600">Assignee: <span style={{ color: '#334155' }}>{item.assigned_to_name}</span></Typography>}
                                      <Typography variant="caption" color="#94a3b8" fontWeight="500">• Logged by: {item.created_by_name || 'System'}</Typography>
                                    </Box>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}><Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditFollowup(item)} sx={{ borderRadius: 2, fontWeight: 'bold', borderColor: '#cbd5e1', bgcolor: 'white', color: '#475569', '&:hover': { bgcolor: '#f8fafc' } }}>Edit</Button></Box>
                              </Box>
                              <CardContent sx={{ p: 3, bgcolor: 'white' }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#334155', lineHeight: 1.6, fontSize: '1rem', mb: 3 }}>{item.description ? (!isAuto ? item.description : item.description.replace('[Next Visit] ', '')) : 'No additional details provided.'}</Typography>
                                {(item.reminder_date || item.date_time_end || item.next_visit || item.next_visit_end) && (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 2.5, borderTop: '1px dashed #e2e8f0' }}>
                                    {item.reminder_date && <Chip icon={<DateRangeIcon fontSize="small" sx={{ color: '#d97706 !important' }} />} label={`Start: ${new Date(item.reminder_date).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`} sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #fef3c7' }} />}
                                    {item.date_time_end && <Chip icon={<AccessTimeIcon fontSize="small" sx={{ color: '#d97706 !important' }} />} label={`End: ${new Date(item.date_time_end).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}`} sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600, borderRadius: 1.5, px: 1, border: '1px solid #fef3c7' }} />}
                                    {(item.next_visit || item.next_visit_end) && <Chip icon={<AccessTimeIcon fontSize="small" sx={{ color: '#7c3aed !important' }} />} label={`Next Visit: ${item.next_visit ? new Date(item.next_visit).toLocaleDateString() : ''} ${item.next_visit_end ? '- ' + new Date(item.next_visit_end).toLocaleDateString() : ''}`} sx={{ bgcolor: '#f5f3ff', color: '#6d28d9', fontWeight: 700, borderRadius: 1.5, px: 1, border: '1px solid #ede9fe' }} />}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 3: Conversations */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Discussion</Typography>
                </Box>
                <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', height: 600, bgcolor: "#f8fafc", overflow: 'hidden' }}>
                  <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {quoteConversations.length === 0 && (
                      <Box sx={{ textAlign: 'center', my: 'auto', color: '#94a3b8' }}>
                        <ForumIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" fontWeight="700" color="#64748b">No discussions yet</Typography>
                        <Typography variant="body2">Be the first to start the conversation.</Typography>
                      </Box>
                    )}
                    {quoteConversations.map((msg) => (
                      <Box key={msg.id} sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, '&:hover .msg-time': { opacity: 1 } }}>
                        <Avatar sx={{ bgcolor: "#3b82f6", width: 40, height: 40, boxShadow: '0 2px 8px rgba(59,130,246,0.3)', fontWeight: 'bold' }}>{msg.created_by_name ? msg.created_by_name.charAt(0).toUpperCase() : 'U'}</Avatar>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '80%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5, ml: 1 }}>
                            <Typography variant="subtitle2" fontWeight="700" color="#334155">{msg.created_by_name || 'User'}</Typography>
                            <Typography className="msg-time" variant="caption" color="#94a3b8" sx={{ opacity: 0.7, transition: 'opacity 0.2s' }}>{new Date(msg.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</Typography>
                          </Box>
                          <Paper sx={{ p: 2.5, borderRadius: '20px 20px 20px 4px', bgcolor: 'white', color: '#1e293b', boxShadow: '0 4px 14px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9' }}><Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.95rem' }}>{msg.message}</Typography></Paper>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ p: 2.5, bgcolor: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField fullWidth variant="outlined" placeholder="Type a message..." value={newConversation} onChange={(e) => setNewConversation(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddConversation(); } }} multiline maxRows={4} InputProps={{ sx: { borderRadius: 3, bgcolor: "#f8fafc", '& fieldset': { borderColor: '#e2e8f0' }, '&:hover fieldset': { borderColor: '#cbd5e1' }, '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px' } } }} />
                    <IconButton onClick={handleAddConversation} disabled={!newConversation.trim()} sx={{ bgcolor: "#3b82f6", color: 'white', width: 48, height: 48, boxShadow: '0 4px 12px rgba(59,130,246,0.3)', transition: 'all 0.2s ease', '&:hover': { bgcolor: '#2563eb', transform: 'scale(1.05)' }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' } }}><SendIcon /></IconButton>
                  </Box>
                </Paper>
              </Box>
            </TabPanel>

            {/* Tab 4: Documents */}
            <TabPanel value={tabValue} index={4}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Documents</Typography>
                  <Button variant="contained" startIcon={uploadingFile ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />} component="label" disabled={uploadingFile} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' }, '&.Mui-disabled': { background: '#94a3b8', color: '#f1f5f9' } }}>{uploadingFile ? 'Uploading...' : 'Upload Document'}<input type="file" hidden onChange={handleFileChange} /></Button>
                </Box>
                {quoteDocuments.length === 0 ? (
                  <Paper elevation={0} component="label" sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1', cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: '#3b82f6', bgcolor: '#eff6ff', transform: 'translateY(-2px)' } }}>
                    <CloudUploadIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
                    <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>No documents found</Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Click here to upload specifications, contracts, or other quote files.</Typography>
                    <Button variant="contained" component="span" sx={{ borderRadius: 2, fontWeight: 'bold', px: 4 }}>Browse Files</Button>
                    <input type="file" hidden onChange={handleFileChange} />
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {quoteDocuments.map((doc) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
                        <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 4, display: 'flex', flexDirection: 'column', height: '100%', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', bgcolor: 'white', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 24px rgba(0,0,0,0.06)', borderColor: '#93c5fd' } }}>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: 4, pb: 2 }}>
                            <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 72, height: 72, mb: 2.5, boxShadow: '0 4px 12px rgba(59,130,246,0.15)' }}><InsertDriveFileIcon sx={{ fontSize: 36 }} /></Avatar>
                            <Tooltip title={doc.file_name} arrow placement="top"><Typography variant="subtitle1" fontWeight="700" color="#1e293b" noWrap sx={{ width: '100%', mb: 1, px: 2 }}>{doc.file_name}</Typography></Tooltip>
                            <Typography variant="caption" color="#94a3b8" fontWeight="600">{new Date(doc.uploaded_at || Date.now()).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</Typography>
                          </CardContent>
                          <Divider sx={{ borderColor: '#f1f5f9' }} />
                          <CardActions sx={{ justifyContent: 'center', p: 0 }}>
                            <Button color="primary" startIcon={<DownloadIcon />} fullWidth sx={{ py: 1.5, borderRadius: '0 0 16px 16px', fontWeight: '700', letterSpacing: 0.5, color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }} href={`${API_URL}/quotes/${id}/documents/${doc.id}/download`} target="_blank" rel="noopener noreferrer">Download</Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </TabPanel>

            {/* Tab 5: Assignments */}
            <TabPanel value={tabValue} index={5}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Team Members</Typography>
                  <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenAssignDialog(true)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' } }}>Add Member</Button>
                </Box>
                {quoteAssignments.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1' }}>
                    <PersonAddIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>No team members assigned</Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Assign users to this quote to track responsibilities.</Typography>
                    <Button variant="outlined" onClick={() => setOpenAssignDialog(true)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>Assign Someone</Button>
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {quoteAssignments.map((assignment) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={assignment.id || assignment.user_id}>
                        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', bgcolor: 'white', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 12px 24px rgba(0,0,0,0.06)', borderColor: '#93c5fd' } }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1, mb: 3 }}>
                              <Avatar sx={{ bgcolor: '#e0e7ff', color: '#2563eb', fontWeight: '800', width: 64, height: 64, mb: 1, fontSize: '1.5rem', boxShadow: '0 4px 12px rgba(37,99,235,0.15)' }}>{assignment.user_name ? assignment.user_name[0].toUpperCase() : 'U'}</Avatar>
                              <Typography variant="h6" fontWeight="800" color="#1e293b" noWrap sx={{ width: '100%' }}>{assignment.user_name || `User ID: ${assignment.user_id}`}</Typography>
                              <Chip label="Team Member" size="small" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} />
                            </Box>
                            <Divider sx={{ borderColor: '#f8fafc', mb: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                <Typography variant="caption" color="#64748b" fontWeight="600" letterSpacing={0.5}>ASSIGNED: {new Date(assignment.assigned_at || Date.now()).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </TabPanel>

            {/* Tab 6: Milestones */}
            <TabPanel value={tabValue} index={6}>
              <Box sx={{ mt: 1, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h5" fontWeight="800" color="#1e293b">Quote Milestones</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenMilestoneDialog(true)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, boxShadow: '0 4px 14px rgba(25,118,210,0.3)', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(25,118,210,0.4)', background: 'linear-gradient(45deg, #1565c0 0%, #1e88e5 100%)' } }}>Add Milestone</Button>
                </Box>
                <Box sx={{ position: 'relative' }}>
                  {quoteMilestones && quoteMilestones.length > 0 && <Box sx={{ position: 'absolute', left: 28, top: 20, bottom: 20, width: 3, bgcolor: '#e2e8f0', zIndex: 0, display: { xs: 'none', md: 'block' }, borderRadius: 2 }} />}
                  <Grid container spacing={3}>
                    {quoteMilestones && quoteMilestones.map((milestone) => (
                      <Grid item xs={12} key={milestone.id}>
                        <Card elevation={0} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: 'white', borderLeft: milestone.status === 'Completed' ? '6px solid #10b981' : '6px solid #3b82f6', ml: { md: 6 }, position: 'relative', overflow: 'visible', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px rgba(0,0,0,0.06)', borderColor: '#93c5fd' } }}>
                          <Box sx={{ position: 'absolute', left: -34 - 7, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20, borderRadius: '50%', bgcolor: milestone.status === 'Completed' ? '#10b981' : '#3b82f6', border: '4px solid white', boxShadow: '0 0 0 4px #f8fafc, 0 4px 6px rgba(0,0,0,0.1)', zIndex: 1, display: { xs: 'none', md: 'block' }, transition: 'all 0.3s' }} />
                          <Box sx={{ flexGrow: 1, ml: { xs: 0, md: 1 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Typography variant="h6" fontWeight="800" color="#1e293b">{milestone.name}</Typography>
                              <Chip label={milestone.status === 'Completed' ? 'Completed' : 'In Progress'} size="small" icon={milestone.status === 'Completed' ? <CheckCircleIcon style={{ color: '#059669' }} /> : <AccessTimeIcon style={{ color: '#2563eb' }} />} sx={{ fontWeight: 700, bgcolor: milestone.status === 'Completed' ? '#d1fae5' : '#dbeafe', color: milestone.status === 'Completed' ? '#065f46' : '#1e40af', height: 26, '& .MuiChip-icon': { color: milestone.status === 'Completed' ? '#059669' : '#2563eb' } }} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#64748b', bgcolor: "#f8fafc", px: 1.5, py: 0.5, borderRadius: 2 }}>
                                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption" fontWeight="700" letterSpacing={0.5}>DUE: {milestone.due_date ? new Date(milestone.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'No deadline'}</Typography>
                              </Box>
                              {milestone.status !== 'Completed' && <Box sx={{ width: 180, flexGrow: { xs: 1, md: 0 } }}><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}><Typography variant="caption" color="#64748b" fontWeight="600">Timeline Progress</Typography></Box><LinearProgress variant="determinate" value={50} sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4, background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)' } }} /></Box>}
                            </Box>
                          </Box>
                          <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', md: 'block' }, borderColor: '#f1f5f9' }} />
                          <Divider sx={{ my: 2, display: { xs: 'block', md: 'none' }, borderColor: '#f1f5f9' }} />
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  {(!quoteMilestones || quoteMilestones.length === 0) && (
                    <Paper elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1', mt: 2 }}>
                      <FlagIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                      <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>No milestones set</Typography>
                      <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>Define project checkpoints and tracking dates for this quote.</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenMilestoneDialog(true)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>Add First Milestone</Button>
                    </Paper>
                  )}
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 7: AI Analyze */}
            <TabPanel value={tabValue} index={7}>
              <Box sx={{ mt: 1, p: 2 }}>
             

                {/* Loading State */}
                {aiAnalyzeLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 3 }}>
                    <CircularProgress size={48} sx={{ color: '#3b82f6' }} />
                    <Typography color="#64748b" fontWeight={600}>Running AI analysis on this quote...</Typography>
                    <LinearProgress sx={{ width: 300, maxWidth: '80%', borderRadius: 2, height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
                  </Box>
                )}

                {/* Error State */}
                {aiAnalyzeError && !aiAnalyzeLoading && (
                  <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #fecaca', bgcolor: '#fef2f2', p: 4, textAlign: 'center' }}>
                    <WarningAmberIcon sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
                    <Typography fontWeight="700" color="#b91c1c" mb={2}>Analysis Error</Typography>
                    <Typography variant="body2" color="#ef4444" mb={3}>{aiAnalyzeError}</Typography>
                    <Button variant="outlined" color="error" onClick={fetchAiAnalyze} sx={{ borderRadius: 2, fontWeight: 'bold' }}>
                      Retry
                    </Button>
                  </Card>
                )}

                {/* Empty State — No data yet, not loading (should not appear since it auto-loads) */}
                {!aiAnalyzeLoading && !aiAnalyzeError && !aiAnalyzeData && (
                  <Paper elevation={0} sx={{ p: 8, textAlign: 'center', bgcolor: "#f8fafc", borderRadius: 4, border: '2px dashed #cbd5e1' }}>
                    <PsychologyIcon sx={{ fontSize: 64, color: '#93c5fd', mb: 2 }} />
                    <Typography variant="h6" color="#334155" fontWeight="700" gutterBottom>
                      AI-Powered Quote Analysis
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>
                      Analysis will load automatically...
                    </Typography>
                  </Paper>
                )}

                {/* Results State */}
                {aiAnalyzeData && !aiAnalyzeLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Win Probability Card */}
                    {winPrediction && winPrediction.win_probability != null && (
                      <Card elevation={0} sx={{
                        borderRadius: 4, border: '1px solid #bfdbfe', bgcolor: 'white', overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(59,130,246,0.08)'
                      }}>
                        <Box sx={{
                          p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
                          background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 56, height: 56, boxShadow: '0 4px 14px rgba(59,130,246,0.3)' }}>
                              <PsychologyIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="h4" fontWeight="900" color="#1e40af">
                                {winPrediction.win_probability}%
                              </Typography>
                              <Typography variant="caption" fontWeight="700" color="#3b82f6" textTransform="uppercase" letterSpacing={0.5}>
                                AI Win Probability
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ flex: 1, maxWidth: 300 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="#1e40af" fontWeight="600">Confidence Level</Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={winPrediction.win_probability}
                              sx={{
                                height: 10, borderRadius: 5, bgcolor: '#bfdbfe',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: winPrediction.win_probability >= 50 ? '#10b981' : '#f59e0b',
                                  borderRadius: 5
                                }
                              }}
                            />
                          </Box>
                        </Box>
                      </Card>
                    )}

                    {/* Explanation (from analyze_quotation) */}
                    {aiAnalyzeData.explanation && (
                      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #fef3c7', bgcolor: '#fffbeb', overflow: 'hidden', boxShadow: '0 4px 20px rgba(245,158,11,0.06)' }}>
                        <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <PsychologyIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                          <Typography variant="subtitle2" fontWeight="800" color="#b45309" textTransform="uppercase" letterSpacing={0.5}>
                            AI Explanation
                          </Typography>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="body2" sx={{ color: '#78350f', lineHeight: 1.7, fontWeight: 500 }}>
                            {aiAnalyzeData.explanation}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}

                    {/* Primary Negotiation Leverage */}
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #d1fae5', bgcolor: '#f0fdf4', overflow: 'hidden', boxShadow: '0 4px 20px rgba(16,185,129,0.06)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#10b981', color: 'white', width: 40, height: 40 }}>
                            <TrendingUpIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="800" color="#065f46" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>
                              Primary Negotiation Leverage
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#064e3b', lineHeight: 1.6 }}>
                              {aiAnalyzeData.ai_summary?.primary_negotiation_leverage || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Price Elasticity Insight */}
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #dbeafe', bgcolor: '#eff6ff', overflow: 'hidden', boxShadow: '0 4px 20px rgba(59,130,246,0.06)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 40, height: 40 }}>
                            <AttachMoneyIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>
                              Price Elasticity Insight
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1e3a5f', lineHeight: 1.6 }}>
                              {aiAnalyzeData.ai_summary?.price_elasticity_insight || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Rationale */}
                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', bgcolor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 40, height: 40 }}>
                            <InfoIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5} mb={0.5}>
                              Analysis Rationale
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6 }}>
                              {aiAnalyzeData.ai_summary?.rationale || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Key Drivers & Risk Flags side by side */}
                    <Grid container spacing={3}>
                      {/* Key Drivers */}
                      <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #d1fae5', bgcolor: '#f0fdf4', height: '100%', boxShadow: '0 4px 20px rgba(16,185,129,0.06)' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                              <CheckCircleIcon sx={{ color: '#10b981', fontSize: 24 }} />
                              <Typography variant="subtitle2" fontWeight="800" color="#065f46" textTransform="uppercase" letterSpacing={0.5}>
                                Key Drivers
                              </Typography>
                            </Box>
                            {aiAnalyzeData.ai_summary?.key_drivers && aiAnalyzeData.ai_summary.key_drivers.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {aiAnalyzeData.ai_summary.key_drivers.map((driver, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #d1fae5' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', mt: 0.75, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ color: '#064e3b', lineHeight: 1.5, fontSize: '0.85rem' }}>
                                      {driver}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="#94a3b8">No key drivers identified.</Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Risk Flags */}
                      <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #fecaca', bgcolor: '#fef2f2', height: '100%', boxShadow: '0 4px 20px rgba(239,68,68,0.06)' }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                              <WarningAmberIcon sx={{ color: '#ef4444', fontSize: 24 }} />
                              <Typography variant="subtitle2" fontWeight="800" color="#b91c1c" textTransform="uppercase" letterSpacing={0.5}>
                                Risk Flags
                              </Typography>
                            </Box>
                            {aiAnalyzeData.ai_summary?.risk_flags && aiAnalyzeData.ai_summary.risk_flags.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {aiAnalyzeData.ai_summary.risk_flags.map((risk, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, bgcolor: 'white', borderRadius: 2, border: '1px solid #fecaca' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', mt: 0.75, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ color: '#7f1d1d', lineHeight: 1.5, fontSize: '0.85rem' }}>
                                      {risk}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="#94a3b8">No risk flags identified.</Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Recommendations */}
                    <Card elevation={0} sx={{
                      borderRadius: 4, border: '1px solid #bfdbfe', bgcolor: 'white', overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(59,130,246,0.08)'
                    }}>
                      <Box sx={{
                        p: 2.5, background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        display: 'flex', alignItems: 'center', gap: 1.5
                      }}>
                        <PsychologyIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
                        <Typography variant="subtitle2" fontWeight="800" color="#1e40af" textTransform="uppercase" letterSpacing={0.5}>
                          AI Recommendations
                        </Typography>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        {aiAnalyzeData.ai_summary?.recommendations && aiAnalyzeData.ai_summary.recommendations.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {aiAnalyzeData.ai_summary.recommendations.map((rec, idx) => (
                              <Box key={idx} sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                                p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0'
                              }}>
                                <Box sx={{
                                  width: 24, height: 24, borderRadius: '50%', bgcolor: '#3b82f6', color: 'white',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontWeight: 800, fontSize: '0.75rem', flexShrink: 0, mt: 0.25
                                }}>
                                  {idx + 1}
                                </Box>
                                <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.6, fontWeight: 600, fontSize: '0.85rem' }}>
                                  {rec}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="#94a3b8">No recommendations available.</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </TabPanel>

          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #f0f0f0', mb: 2 }}>Assign Team Member</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1, minHeight: 200 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Select a user to assign to this quote. They will receive notifications and updates.</Typography>
          <Autocomplete fullWidth options={users} getOptionLabel={(option) => option ? (option.full_name || option.username || '') : ''} value={users.find(u => String(u.id) === String(newAssignment)) || null} onChange={(event, newValue) => { setNewAssignment(newValue ? newValue.id : ''); }} renderInput={(params) => (<TextField {...params} label="Search User" variant="outlined" />)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setOpenAssignDialog(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained" disabled={assignLoading || !newAssignment} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }}>{assignLoading ? 'Assigning...' : 'Assign User'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMilestoneDialog} onClose={() => setOpenMilestoneDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', borderBottom: '1px solid #f0f0f0', mb: 2 }}>Add Milestone</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField autoFocus label="Milestone Name" fullWidth variant="outlined" value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)} />
          <TextField label="Due Date" type="date" fullWidth variant="outlined" InputLabelProps={{ shrink: true }} value={newMilestoneDate} onChange={(e) => setNewMilestoneDate(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setOpenMilestoneDialog(false)} sx={{ color: '#64748b', fontWeight: 'bold' }}>Cancel</Button>
          <Button onClick={handleAddMilestone} variant="contained" disabled={!newMilestone} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3 }}>Add Milestone</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openFollowupDialog} onClose={() => setOpenFollowupDialog(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 700, overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', p: 3, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
          <EventNoteIcon sx={{ fontSize: 32, opacity: 0.9 }} />
          <Box><Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>{editingFollowupId ? 'Edit Follow-up' : 'Log a Follow-up'}</Typography><Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>{editingFollowupId ? "Update details for this scheduled interaction." : "Record a new interaction or log an upcoming event."}</Typography></Box>
        </Box>
        <DialogContent sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
            <Typography variant="subtitle2" color="#64748b" fontWeight="800" textTransform="uppercase" mb={3}>Interaction Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}><TextField fullWidth label="Title / Short Note *" value={newFollowup} onChange={(e) => setNewFollowup(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12}><Autocomplete fullWidth options={users} getOptionLabel={(option) => option ? (option.full_name || option.username || '') : ''} value={users.find(u => String(u.id) === String(newFollowupAssignee)) || null} onChange={(e, val) => setNewFollowupAssignee(val ? val.id : '')} renderInput={(params) => <TextField {...params} label="Assign To / Visibility" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Detailed Description (Optional)" multiline rows={3} value={newFollowupDesc} onChange={(e) => setNewFollowupDesc(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
            </Grid>
          </Paper>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}><DateRangeIcon sx={{ color: '#3b82f6' }} /><Typography variant="subtitle2" color="#1e293b" fontWeight="800" textTransform="uppercase">Current Interaction Timing</Typography></Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={newFollowupStartDate} onChange={(e) => setNewFollowupStartDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="Start Time" InputLabelProps={{ shrink: true }} value={newFollowupStartTime} onChange={(e) => setNewFollowupStartTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="End Date" InputLabelProps={{ shrink: true }} value={newFollowupEndDate} onChange={(e) => setNewFollowupEndDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="End Time" InputLabelProps={{ shrink: true }} value={newFollowupEndTime} onChange={(e) => setNewFollowupEndTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} /></Grid>
            </Grid>
          </Paper>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #ede9fe', bgcolor: '#f5f3ff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}><AccessTimeIcon sx={{ color: '#7c3aed' }} /><Typography variant="subtitle2" color="#5b21b6" fontWeight="800" textTransform="uppercase">Plan Next Visit</Typography></Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="Next Visit Start Date" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitStartDate} onChange={(e) => setNewFollowupNextVisitStartDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="Next Visit Start Time" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitStartTime} onChange={(e) => setNewFollowupNextVisitStartTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="date" label="Next Visit End Date" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitEndDate} onChange={(e) => setNewFollowupNextVisitEndDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
              <Grid item xs={12} sm={3}><TextField fullWidth type="time" label="Next Visit End Time" InputLabelProps={{ shrink: true }} value={newFollowupNextVisitEndTime} onChange={(e) => setNewFollowupNextVisitEndTime(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }} /></Grid>
            </Grid>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#6d28d9', fontStyle: 'italic', fontWeight: 500 }}>* Automatically creates a dedicated follow-up for the next visit dates provided.</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid #f1f5f9', bgcolor: 'white' }}>
          <Button onClick={() => setOpenFollowupDialog(false)} sx={{ borderRadius: 2, fontWeight: 'bold', color: '#64748b', px: 3 }}>Cancel</Button>
          <Button onClick={handleAddFollowup} variant="contained" disabled={!newFollowup} sx={{ borderRadius: 2, fontWeight: 'bold', px: 4, py: 1, boxShadow: 'none', background: 'linear-gradient(45deg, #1976d2 0%, #42a5f5 100%)', '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' } }}>{editingFollowupId ? 'Update' : 'Save Follow-up'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuoteDetails;